import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { IoNotificationsOutline } from "react-icons/io5";
import {
  useGetNotificationsQuery,
  useGetNotificationsUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetNotificationPreferencesQuery,
  useSetNotificationPreferencesMutation,
} from "../../store";
import { useAlert } from "../../context/AlertContext";
import NotificationPanel from "./NotificationPanel";
import useNotificationAccess from "./useNotificationAccess";
import { NOTIFICATION_LIMIT, UNREAD_POLL_MS, formatBadge, taskPath, devicePath } from "./notificationConstants";

const PANEL_WIDTH = 360;
const MOBILE_BREAKPOINT = 768;

// Panel konumu: masaüstünde düğmenin sağına (sol ray), mobilde ekranın üstüne tam genişlik.
function computePanelStyle(anchor) {
  if (typeof window === "undefined" || !anchor) return {};
  if (window.innerWidth < MOBILE_BREAKPOINT) return { left: 12, right: 12, top: 64 };
  const rect = anchor.getBoundingClientRect();
  const top = Math.max(8, Math.min(rect.top, window.innerHeight * 0.3));
  return { left: rect.right + 8, top, width: PANEL_WIDTH };
}

/**
 * İş Planlama bildirim zili — okunmamış rozeti + açılır liste.
 * Contract: BayinerIOT-Workspace-/docs/api-contracts/tasks.md → "Bildirimler (1.2)".
 *
 * @param {boolean} socketConnected — true iken okunmamış sayısı sorgulanmaz (socket günceller);
 *   false iken 60 sn'de bir `unreadCount`.
 * @param {boolean} showLabel — sidebar açıkken "Bildirimler" etiketi.
 * @param {Function} onNavigate — bildirime tıklanıp sayfaya gidildiğinde (mobil menüyü kapatmak için).
 */
export default function NotificationBell({
  socketConnected = false,
  showLabel = false,
  onNavigate,
  className = "",
  open: openProp,
  onOpenChange,
  onPanelMouseEnter,
  onPanelMouseLeave,
}) {
  const { enabled, token, scope, canTasks } = useNotificationAccess();
  const navigate = useNavigate();
  const { showError } = useAlert() || {};
  // Kontrollü kullanımda (masaüstü ray, #412) açık/kapalı durumunu menü yönetir:
  // Son İşlemler ile aynı anda açılmaz, menü daralınca kapanır. Mobilde kendi durumu.
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = useCallback(
    (value) => (controlled ? onOpenChange?.(value) : setOpenState(value)),
    [controlled, onOpenChange],
  );
  const [panelStyle, setPanelStyle] = useState({});
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const skip = !enabled || !token;
  const { data: countData } = useGetNotificationsUnreadCountQuery(
    { token, scope },
    { skip, pollingInterval: socketConnected ? 0 : UNREAD_POLL_MS },
  );
  const listQuery = useGetNotificationsQuery(
    { token, scope, limit: NOTIFICATION_LIMIT },
    { skip: skip || !open, refetchOnMountOrArgChange: 30 },
  );
  // Tek tercih İş Planlama e-postası (#129) — yetkisi yoksa sorgu da yok.
  const prefsQuery = useGetNotificationPreferencesQuery({ token, scope }, { skip: skip || !open || !canTasks });
  const [markRead] = useMarkNotificationsReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [setPreferences] = useSetNotificationPreferencesMutation();

  const close = useCallback(() => setOpen(false), [setOpen]);

  useLayoutEffect(() => {
    if (open) setPanelStyle(computePanelStyle(buttonRef.current));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  if (!enabled) return null;

  const unreadCount = countData?.data?.unreadCount ?? listQuery.data?.data?.unreadCount ?? 0;
  const badge = formatBadge(unreadCount);
  const notifications = listQuery.data?.data?.notifications || [];

  const handleOpen = (notification) => {
    const target = notification?.task?.key
      ? taskPath(notification.task.key)
      : notification?.device?._id
        ? devicePath(scope, notification.device._id)
        : null;
    if (!target) return;
    if (!notification.readAt) {
      Promise.resolve(markRead({ token, scope, ids: [notification._id] })).catch(() => {});
    }
    close();
    onNavigate?.();
    navigate(target);
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead({ token, scope }).unwrap();
    } catch (err) {
      showError?.(err?.data?.message || "Bildirimler işaretlenemedi.");
    }
  };

  // field: "tasksEmail"
  const handleToggleEmail = async (field, value) => {
    try {
      await setPreferences({ token, scope, [field]: value }).unwrap();
    } catch (err) {
      showError?.(err?.data?.message || "Tercih kaydedilemedi.");
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={badge ? `Bildirimler (${badge} okunmamış)` : "Bildirimler"}
        aria-expanded={open}
        title={!showLabel ? "Bildirimler" : undefined}
        className={`group flex items-center transition-colors duration-200 ${
          open ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"
        } ${className}`}
      >
        <span className="relative flex items-center gap-3.5">
          <span className={`relative ${open ? "text-indigo-600" : "text-gray-400 group-hover:text-gray-700"}`}>
            <IoNotificationsOutline className="h-[22px] w-[22px]" />
            {badge && (
              <span
                data-testid="notification-badge"
                className="absolute -right-2 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-fifth px-1 text-[10px] font-Bold leading-none text-white ring-2 ring-white"
              >
                {badge}
              </span>
            )}
          </span>
          {showLabel && <span className="truncate text-[15px] font-Default">Bildirimler</span>}
        </span>
      </button>
      {open &&
        createPortal(
          <NotificationPanel
            ref={panelRef}
            style={panelStyle}
            onMouseEnter={onPanelMouseEnter}
            onMouseLeave={onPanelMouseLeave}
            notifications={notifications}
            isLoading={listQuery.isLoading}
            isError={listQuery.isError}
            unreadCount={unreadCount}
            onOpen={handleOpen}
            onMarkAll={handleMarkAll}
            emailPrefs={[
              canTasks && {
                field: "tasksEmail",
                label: "İş Planlama e-postaları",
                checked: prefsQuery.data?.data?.tasksEmail ?? true,
              },
            ].filter(Boolean)}
            prefsLoading={prefsQuery.isLoading}
            onToggleEmail={handleToggleEmail}
          />,
          document.body,
        )}
    </>
  );
}
